import { createTheme } from '@mui/material/styles';

// Cyberpunk color palette
export const cyberpunkColors = {
  neonCyan: '#00ffff',
  neonMagenta: '#ff00ff',
  neonPink: '#ff1493',
  neonGreen: '#00ff41',
  darkBg: '#0a0a0f',
  darkSurface: '#121218',
  darkPaper: '#1a1a24',
  darkCard: '#0d0d14',
  textPrimary: '#e0e0e0',
  textSecondary: '#a0a0a0',
  success: '#00ff41',
  warning: '#ffaa00',
  error: '#ff0055',
};

// Glow effects
export const glowEffects = {
  cyan: '0 0 10px #00ffff, 0 0 20px #00ffff, 0 0 30px #00ffff',
  magenta: '0 0 10px #ff00ff, 0 0 20px #ff00ff, 0 0 30px #ff00ff',
  cyanSubtle: '0 0 5px rgba(0, 255, 255, 0.5)',
  magentaSubtle: '0 0 5px rgba(255, 0, 255, 0.5)',
};

const cyberpunkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: cyberpunkColors.neonCyan,
      light: '#66ffff',
      dark: '#00cccc',
      contrastText: '#0a0a0f',
    },
    secondary: {
      main: cyberpunkColors.neonMagenta,
      light: '#ff66ff',
      dark: '#cc00cc',
      contrastText: '#0a0a0f',
    },
    background: {
      default: cyberpunkColors.darkBg,
      paper: cyberpunkColors.darkPaper,
    },
    text: {
      primary: cyberpunkColors.textPrimary,
      secondary: cyberpunkColors.textSecondary,
    },
    success: {
      main: cyberpunkColors.success,
    },
    warning: {
      main: cyberpunkColors.warning,
    },
    error: {
      main: cyberpunkColors.error,
    },
    info: {
      main: cyberpunkColors.neonCyan,
      dark: '#008b8b',
    },
    divider: 'rgba(0, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Rajdhani", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.1em',
    },
    h2: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.08em',
    },
    h3: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h4: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
    h5: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.03em',
    },
    h6: {
      fontFamily: '"Orbitron", sans-serif',
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    button: {
      fontFamily: '"Rajdhani", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: `linear-gradient(135deg, ${cyberpunkColors.darkBg} 0%, #1a0a1a 50%, ${cyberpunkColors.darkBg} 100%)`,
          minHeight: '100vh',
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            pointerEvents: 'none',
            zIndex: -1,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${cyberpunkColors.neonCyan} 0%, ${cyberpunkColors.neonMagenta} 100%)`,
          color: cyberpunkColors.darkBg,
          fontWeight: 700,
          '&:hover': {
            background: `linear-gradient(135deg, ${cyberpunkColors.neonMagenta} 0%, ${cyberpunkColors.neonCyan} 100%)`,
            boxShadow: `0 0 20px ${cyberpunkColors.neonCyan}, 0 0 40px ${cyberpunkColors.neonMagenta}`,
          },
        },
        outlined: {
          borderColor: cyberpunkColors.neonCyan,
          color: cyberpunkColors.neonCyan,
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            borderColor: cyberpunkColors.neonMagenta,
            color: cyberpunkColors.neonMagenta,
            boxShadow: glowEffects.magentaSubtle,
            backgroundColor: 'rgba(255, 0, 255, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: `linear-gradient(145deg, ${cyberpunkColors.darkPaper} 0%, ${cyberpunkColors.darkCard} 100%)`,
          border: `1px solid rgba(0, 255, 255, 0.2)`,
          boxShadow: '0 0 15px rgba(0, 255, 255, 0.1), inset 0 0 20px rgba(0, 255, 255, 0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: cyberpunkColors.neonCyan,
            boxShadow: '0 0 25px rgba(0, 255, 255, 0.2), inset 0 0 30px rgba(0, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: cyberpunkColors.darkPaper,
        },
        elevation1: {
          boxShadow: '0 0 10px rgba(0, 255, 255, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(90deg, ${cyberpunkColors.darkBg} 0%, ${cyberpunkColors.darkSurface} 50%, ${cyberpunkColors.darkBg} 100%)`,
          borderBottom: `1px solid rgba(0, 255, 255, 0.3)`,
          boxShadow: `0 0 20px rgba(0, 255, 255, 0.2)`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(0, 255, 255, 0.3)',
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: cyberpunkColors.neonCyan,
            },
            '&.Mui-focused fieldset': {
              borderColor: cyberpunkColors.neonMagenta,
              boxShadow: glowEffects.magentaSubtle,
            },
          },
          '& .MuiFilledInput-root': {
            backgroundColor: 'rgba(0, 255, 255, 0.05)',
            borderRadius: 4,
            border: '1px solid rgba(0, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(0, 255, 255, 0.08)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 0, 255, 0.05)',
              border: `1px solid ${cyberpunkColors.neonMagenta}`,
            },
            '&::before, &::after': {
              display: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: cyberpunkColors.textSecondary,
            '&.Mui-focused': {
              color: cyberpunkColors.neonMagenta,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: cyberpunkColors.darkPaper,
          border: `1px solid ${cyberpunkColors.neonCyan}`,
          boxShadow: `0 0 20px rgba(0, 255, 255, 0.3)`,
        },
        option: {
          '&:hover': {
            backgroundColor: 'rgba(0, 255, 255, 0.1)',
          },
          '&[aria-selected="true"]': {
            backgroundColor: 'rgba(255, 0, 255, 0.2)',
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          border: `1px solid rgba(0, 255, 255, 0.2)`,
          borderRadius: 4,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            background: `linear-gradient(180deg, ${cyberpunkColors.darkPaper} 0%, ${cyberpunkColors.darkBg} 100%)`,
            color: cyberpunkColors.neonCyan,
            borderBottom: `2px solid ${cyberpunkColors.neonMagenta}`,
            fontFamily: '"Orbitron", sans-serif',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: 'rgba(0, 255, 255, 0.05)',
            },
            '&:nth-of-type(odd)': {
              backgroundColor: 'rgba(0, 255, 255, 0.02)',
            },
          },
          '& .MuiTableCell-body': {
            borderBottom: '1px solid rgba(0, 255, 255, 0.1)',
            color: cyberpunkColors.textPrimary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Rajdhani", sans-serif',
          fontWeight: 600,
        },
        filled: {
          backgroundColor: 'rgba(0, 255, 255, 0.2)',
          color: cyberpunkColors.neonCyan,
          border: `1px solid ${cyberpunkColors.neonCyan}`,
        },
        outlined: {
          borderColor: cyberpunkColors.neonMagenta,
          color: cyberpunkColors.neonMagenta,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: 'rgba(0, 255, 255, 0.1)',
        },
        bar: {
          borderRadius: 4,
          background: `linear-gradient(90deg, ${cyberpunkColors.neonCyan} 0%, ${cyberpunkColors.neonMagenta} 100%)`,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: cyberpunkColors.neonCyan,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: cyberpunkColors.darkPaper,
          border: `1px solid ${cyberpunkColors.neonCyan}`,
          boxShadow: `0 0 30px rgba(0, 255, 255, 0.3)`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Orbitron", sans-serif',
          color: cyberpunkColors.neonCyan,
          borderBottom: `1px solid rgba(255, 0, 255, 0.3)`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: cyberpunkColors.neonCyan,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: cyberpunkColors.neonMagenta,
            backgroundColor: 'rgba(255, 0, 255, 0.1)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.neon-cyan': {
            color: cyberpunkColors.neonCyan,
            textShadow: glowEffects.cyanSubtle,
          },
          '&.neon-magenta': {
            color: cyberpunkColors.neonMagenta,
            textShadow: glowEffects.magentaSubtle,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: cyberpunkColors.neonMagenta,
          textDecoration: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: cyberpunkColors.neonCyan,
            textShadow: glowEffects.cyanSubtle,
          },
        },
      },
    },
  },
});

export default cyberpunkTheme;
